import { useState, useEffect } from 'react';
import { Paper, Box, Stack, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { RefreshOutlined } from '@mui/icons-material';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useGetCaseAnalyticsQueue } from '../../../../../../../../features/analytics/hooks/useGetCaseAnalyticsQueue';
import FullScreenLoader from '../../../../../../caller/components/fullscreen-loader';
import { dataLabelsDefault, DeliveredBackground, DeliveredBorder, NewRegistrationBackground, NewRegistrationBorder } from '../../utility/graph';
import dayjs from 'dayjs';
import DateRangePicker from '../../../../components/date-range-picker';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export const CaseAnalyticsQueueChart = ({ fromDate: controlledFromDate, toDate: controlledToDate, onFromDateChange, onToDateChange }) => {
    const [localFromDate, setLocalFromDate] = useState(dayjs().startOf('month'));
    const [localToDate, setLocalToDate] = useState(dayjs().endOf('month'));
    const [viewMode, setViewMode] = useState('count'); // 'count' or 'percentage'

    const fromDate = controlledFromDate !== undefined ? controlledFromDate : localFromDate;
    const toDate = controlledToDate !== undefined ? controlledToDate : localToDate;

    useEffect(() => {
        if (controlledFromDate !== undefined) {
            setLocalFromDate(controlledFromDate);
        }
    }, [controlledFromDate]);

    useEffect(() => {
        if (controlledToDate !== undefined) {
            setLocalToDate(controlledToDate);
        }
    }, [controlledToDate]);

    const handleFromDateChange = (newDate) => {
        setLocalFromDate(newDate);
    };

    const handleToDateChange = (newDate) => {
        setLocalToDate(newDate);
    };

    const handleViewModeChange = (event, newViewMode) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode);
        }
    };

    // Always refetch on mount by using a unique key (forces remount/fetch)
    const [fetchKey] = useState(() => Date.now());
    const { data: caseAnalytics, isFetching, refetch } = useGetCaseAnalyticsQueue(
        localFromDate,
        localToDate,
        fetchKey
    );

    // Handle refresh button click
    const handleRefresh = () => {
        refetch();
    };


    // Get data for selected date range
    const getSelectedData = () => {
        const data = caseAnalytics?.data || {
            newRegistration: 0,
            delivered: 0,
            newRegistrationPercentage: 0,
            deliveredPercentage: 0,
            totalCount: 0,
            totalQueueCount: 0,
            totalQueuePercentage: 0,
            deliveredQueue: 0,
            deliveredQueuePercentage: 0,
            newRegistrationQueue: 0,
            newRegistrationQueuePercentage: 0
        };

        const isPercentageView = viewMode === 'percentage';

        // Include both regular and queue data
        const chartData = isPercentageView
            ? [
                data.newRegistrationPercentage,
                data.deliveredPercentage,
                data.newRegistrationQueuePercentage,
                data.deliveredQueuePercentage
            ]
            : [
                data.newRegistration,
                data.delivered,
                data.newRegistrationQueue,
                data.deliveredQueue
            ];

        const dataLabel = isPercentageView ? 'Percentage' : 'Count';

        return {
            labels: ['New Registration', 'Delivered', 'Queue - New Registration', 'Queue - Delivered'],
            datasets: [
                {
                    label: `${fromDate.format('MMM YYYY')} - ${toDate.format('MMM YYYY')} ${dataLabel}`,
                    data: chartData,
                    backgroundColor: [
                        NewRegistrationBackground,
                        DeliveredBackground,
                        'rgba(255, 159, 64, 0.6)',  // Orange for queue new registration
                        'rgba(75, 192, 192, 0.6)'   // Teal for queue delivered
                    ],
                    borderColor: [
                        NewRegistrationBorder,
                        DeliveredBorder,
                        'rgba(255, 159, 64, 1)',    // Orange border for queue new registration
                        'rgba(75, 192, 192, 1)'      // Teal border for queue delivered
                    ],
                    borderWidth: 1,
                }
            ]
        };
    };

    const chartOptions = {
        indexAxis: 'y',
        responsive: true,
        plugins: {
            legend: {
                position: 'bottom',
                display: true,
                onClick: null,
                onHover: (e) => { e.native.target.style.cursor = 'default'; },
                labels: {
                    usePointStyle: true,
                    pointStyle: 'rect',
                    padding: 20,
                    font: {
                        size: 14,
                        weight: 'bold'
                    },
                    color: '#333',
                    generateLabels: function (chart) {
                        const data = chart.data;
                        if (data.datasets.length) {
                            return data.labels.map((label, i) => {
                                const dataset = data.datasets[0];
                                return {
                                    text: label,
                                    fillStyle: dataset.backgroundColor[i],
                                    strokeStyle: dataset.borderColor[i],
                                    lineWidth: 2,
                                    hidden: false,
                                    index: i,
                                    pointStyle: 'rect'
                                };
                            });
                        }
                        return [];
                    }
                }
            },
            title: {
                display: true,
                text: `Case Analytics Queue - ${fromDate.format('MMM YYYY')} to ${toDate.format('MMM YYYY')} (${viewMode === 'percentage' ? 'Percentage' : 'Count'})`,
                font: {
                    size: 16
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.parsed.x;
                        return viewMode === 'percentage'
                            ? `${label}: ${value}%`
                            : `${label}: ${value}`;
                    }
                }
            },
            datalabels: dataLabelsDefault
        },
        scales: {
            x: {
                beginAtZero: true,
                grid: {
                    display: true,
                },
                ticks: {
                    stepSize: viewMode === 'percentage' ? 10 : 200,
                    callback: function (value) {
                        return viewMode === 'percentage' ? `${value}%` : value;
                    }
                }
            },
            y: {
                grid: {
                    display: false,
                }
            }
        },
        maintainAspectRatio: false,
    };

    return (
        <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #d0d0d0' }}>
            <FullScreenLoader open={isFetching} />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <DateRangePicker
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromChange={handleFromDateChange}
                    onToChange={handleToDateChange}
                    disabled={isFetching}
                />
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    disabled={isFetching}
                >
                    <ToggleButton value="count">
                        Count
                    </ToggleButton>
                    <ToggleButton value="percentage">
                        Percentage
                    </ToggleButton>
                </ToggleButtonGroup>
                <Button
                    variant="outlined"
                    startIcon={<RefreshOutlined />}
                    onClick={handleRefresh}
                    disabled={isFetching}
                    size="small"
                    sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                            borderColor: '#1565c0',
                            backgroundColor: '#f3f4f6',
                        },
                        '&:disabled': {
                            borderColor: '#e0e0e0',
                            color: '#9e9e9e',
                        }
                    }}
                >
                    {isFetching ? 'Refreshing...' : 'Refresh'}
                </Button>
            </Stack>
            <Box sx={{ height: 400, width: '100%' }}>
                <Bar data={getSelectedData()} options={chartOptions} />
            </Box>
        </Paper>
    );
};
